const chapter = {
  id: 'work-energy-power',
  slug: 'work-energy-power',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Work, Energy & Power',
        hindi: 'कार्य, ऊर्जा और शक्ति',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'work',
      title: {
        english: 'Work Done by a Force',
        hindi: 'बल द्वारा किया गया कार्य',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Work (constant force)',
        hindi: 'कार्य (निरंतर बल)',
          },
          body: {
            english: '$W = \\vec F \\cdot \\vec d = F d \\cos\\theta$. SI unit: **joule** (J) = N·m. Scalar.',
        hindi: '$W = \\vec F \\cdot \\vec d = F d \\cos\\theta$. एसआई इकाई: **जूल** (जे) = एनएम. अदिश.',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Work (variable force)',
        hindi: 'कार्य (परिवर्तनीय बल)',
          },
          body: {
            english: '$W = \\displaystyle\\int_{\\vec r_1}^{\\vec r_2} \\vec F \\cdot d\\vec r$. Geometrically: **area under the $F$–$x$ graph**.',
        hindi: '$W = \\displaystyle\\int_{\\vec r_1}^{\\vec r_2} \\vec F \\cdot d\\vec r$. ज्यामितीय रूप से: **$F$–$x$ ग्राफ़ के अंतर्गत क्षेत्र**।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Work is **positive** if $0 \\le \\theta < 90°$, **zero** if $\\theta = 90°$, **negative** if $90° < \\theta \\le 180°$.',
        hindi: 'कार्य **सकारात्मक** है यदि $0 \\le \\theta < 90°$, **शून्य** यदि $\\theta = 90°$, **नकारात्मक** यदि $90° < \\theta \\le 180°$ है।',
            },
            {
              english: 'Work done by friction (kinetic) on a sliding body is always **negative**.',
        hindi: 'फिसलते हुए पिंड पर घर्षण (गतिज) द्वारा किया गया कार्य हमेशा **नकारात्मक** होता है।',
            },
            {
              english: 'Work done by **centripetal force** in uniform circular motion = $0$ (force ⟂ displacement).',
        hindi: 'एकसमान गोलाकार motion = (बल ⟂ displacement) में **केन्द्राभिमुख बल** द्वारा किया गया कार्य।',
            },
            {
              english: 'Work done by **internal forces** in a rigid body = $0$.',
        hindi: 'कठोर body = में **आंतरिक बलों** द्वारा किया गया कार्य।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A force $\\vec F = (3\\hat i + 4\\hat j)$ N acts on a particle that moves from $(0,0)$ to $(2, 3)$ m. Find work done.',
        hindi: 'एक बल $\\vec F = (3\\hat i + 4\\hat j)$ N एक कण पर कार्य करता है जो $(0,0)$ से $(2, 3)$ मीटर तक चलता है। काम पूरा हुआ पाया.',
          },
          solution: {
            english: '$W = \\vec F \\cdot \\vec d = 3(2) + 4(3) = 18$ J.',
        hindi: '$W = \\vec F \\cdot \\vec d = 3(2) + 4(3) = 18$ J.',
          },
        },
      ],
    },
    {
      id: 'ke-wet',
      title: {
        english: 'Kinetic Energy & Work–Energy Theorem',
        hindi: 'गतिज ऊर्जा और कार्य-ऊर्जा प्रमेय',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Kinetic energy',
        hindi: 'गतिज ऊर्जा',
          },
          body: {
            english: '$K = \\dfrac{1}{2} m v^2 = \\dfrac{p^2}{2m}$.',
        hindi: '$K = \\dfrac{1}{2} m v^2 = \\dfrac{p^2}{2m}$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Work–energy theorem',
        hindi: 'कार्य-ऊर्जा प्रमेय',
          },
          statement: {
            english: `Net work done by all forces on a particle equals change in its kinetic energy:
$$W_{\\text{net}} = \\Delta K = \\tfrac{1}{2} m v_f^2 - \\tfrac{1}{2} m v_i^2.$$`,
            hindi: 'किसी कण पर सभी बलों द्वारा किया गया कुल कार्य उसकी गतिज ऊर्जा में परिवर्तन के बराबर होता है: $$W_{\\text{net}} = \\Delta K = \\tfrac{1}{2} m v_f^2 - \\tfrac{1}{2} m v_i^2.$$',
          },
          proof: {
            english: 'From $F = m\\dfrac{dv}{dt}$, $W = \\int F\\,dx = \\int m\\dfrac{dv}{dt}\\,dx = \\int m v\\,dv = \\tfrac{1}{2} m(v_f^2 - v_i^2)$.',
        hindi: '$F = m\\dfrac{dv}{dt}$, $W = \\int F\\,dx = \\int m\\dfrac{dv}{dt}\\,dx = \\int m v\\,dv = \\tfrac{1}{2} m(v_f^2 - v_i^2)$ से।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'A 2 kg block at rest is acted on by net force 10 N over 5 m. Find final speed.',
        hindi: 'आराम की स्थिति में 2 किलोग्राम के ब्लॉक पर 5 मीटर से अधिक शुद्ध बल 10 N द्वारा कार्य किया जाता है। अंतिम गति ज्ञात करें. J ।',
          },
          solution: {
            english: '$W = 10 \\cdot 5 = 50$ J $= \\tfrac{1}{2}(2)v^2 \\Rightarrow v = \\sqrt{50} \\approx 7.07$ m/s.',
        hindi: '$W = 10 \\cdot 5 = 50$ J $= \\tfrac{1}{2}(2)v^2 \\Rightarrow v = \\sqrt{50} \\approx 7.07$ मी/से.',
          },
        },
      ],
    },
    {
      id: 'pe-cons',
      title: {
        english: 'Conservative Forces & Potential Energy',
        hindi: 'संरक्षी बल और स्थितिज ऊर्जा',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Conservative force',
        hindi: 'रूढ़िवादी बल',
          },
          body: {
            english: 'Force whose work depends only on initial & final positions (not path). Equivalently, $\\oint \\vec F \\cdot d\\vec r = 0$. Examples: gravity, spring, electrostatic. **Non-conservative**: friction, viscous drag.',
        hindi: 'वह बल जिसका कार्य केवल प्रारंभिक और अंतिम स्थिति पर निर्भर करता है (पथ पर नहीं)। समतुल्य, $\\oint \\vec F \\cdot d\\vec r = 0$. उदाहरण: गुरुत्वाकर्षण, स्प्रिंग, इलेक्ट्रोस्टैटिक। **गैर-रूढ़िवादी**: घर्षण, चिपचिपा खिंचाव।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Potential energy',
        hindi: 'संभावित ऊर्जा',
          },
          statement: {
            english: 'For a conservative force, $U(\\vec r)$ is defined by $\\Delta U = -W_{\\text{cons}} = -\\int \\vec F \\cdot d\\vec r$. In 1D: $F = -\\dfrac{dU}{dx}$.',
        hindi: 'एक रूढ़िवादी बल के लिए, $U(\\vec r)$ को $\\Delta U = -W_{\\text{cons}} = -\\int \\vec F \\cdot d\\vec r$ द्वारा परिभाषित किया गया है। 1डी में: $F = -\\dfrac{dU}{dx}$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Gravitational PE** (near Earth): $U = mgh$ (reference at $h = 0$).',
        hindi: '**गुरुत्वाकर्षण पीई** (पृथ्वी के निकट): $U = mgh$ ($h = 0$ पर संदर्भ)।',
            },
            {
              english: '**Spring PE**: $U = \\tfrac{1}{2} k x^2$.',
        hindi: '**स्प्रिंग पीई**: $U = \\tfrac{1}{2} k x^2$.',
            },
            {
              english: '**Equilibrium**: $dU/dx = 0$. Stable if $d^2U/dx^2 > 0$, unstable if $< 0$, neutral if $= 0$.',
        hindi: '**संतुलन**: $dU/dx = 0$. यदि $d^2U/dx^2 > 0$ है तो स्थिर है, यदि $< 0$ है तो अस्थिर है, यदि $= 0$ है तो तटस्थ है।',
            },
          ],
        },
      ],
    },
    {
      id: 'cons-energy',
      title: {
        english: 'Conservation of Mechanical Energy',
        hindi: 'यांत्रिक ऊर्जा का संरक्षण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Conservation of mechanical energy',
        hindi: 'यांत्रिक ऊर्जा का संरक्षण',
          },
          statement: {
            english: `If only conservative forces do work, $E = K + U = \\text{constant}$. If non-conservative forces (friction etc.) act:
$$\\Delta K + \\Delta U = W_{\\text{non-cons}}.$$`,
            hindi: 'यदि केवल रूढ़िवादी ताकतें ही काम करती हैं, तो $E = K + U = \\text{constant}$। यदि गैर-रूढ़िवादी ताकतें (घर्षण आदि) कार्य करती हैं: $$\\Delta K + \\Delta U = W_{\\text{non-cons}}.$$',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'A block slides down a frictionless incline of height 5 m. Find speed at bottom. ($g = 10$)',
        hindi: 'एक ब्लॉक 5 मीटर ऊंचाई की घर्षण रहित ढलान पर नीचे की ओर फिसलता है। नीचे गति ज्ञात करें. ($g = 10$)',
          },
          solution: {
            english: '$mgh = \\tfrac12 m v^2 \\Rightarrow v = \\sqrt{2gh} = \\sqrt{100} = 10$ m/s.',
        hindi: '$mgh = \\tfrac12 m v^2 \\Rightarrow v = \\sqrt{2gh} = \\sqrt{100} = 10$ मी/से.',
          },
        },
        {
          type: 'tip',
          text: {
            english: '**Vertical loop / valley** problems: combine energy conservation with $v^2 \\ge gr$ at top of loop, $N = m(v^2/r + g)$ at bottom etc.',
        hindi: '**वर्टिकल लूप/वैली** समस्याएं: ऊर्जा संरक्षण को लूप के शीर्ष पर $v^2 \\ge gr$, नीचे $N = m(v^2/r + g)$ आदि के साथ संयोजित करें।',
          },
        },
      ],
    },
    {
      id: 'power',
      title: {
        english: 'Power',
        hindi: 'शक्ति',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Power',
        hindi: 'शक्ति',
          },
          body: {
            english: 'Rate of doing work: $P_{\\text{avg}} = W/t$, $P_{\\text{inst}} = dW/dt = \\vec F \\cdot \\vec v$. SI unit: **watt** (W) = J/s. 1 hp = 746 W.',
        hindi: 'कार्य करने की दर: $P_{\\text{avg}} = W/t$, $P_{\\text{inst}} = dW/dt = \\vec F \\cdot \\vec v$. एसआई इकाई: **वाट** (डब्ल्यू) = जे/एस। 1 एचपी = 746 डब्ल्यू.',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'A 1500 kg car accelerates from 0 to 20 m/s in 10 s. Average power delivered (neglect friction)?',
        hindi: '1500 किलोग्राम की एक कार 10 सेकंड में 0 से 20 m/s तक की गति पकड़ लेती है। वितरित औसत शक्ति (घर्षण की उपेक्षा)?',
          },
          solution: {
            english: '$\\Delta K = \\tfrac12 (1500)(400) = 3 \\times 10^5$ J. $P = 3\\times 10^5/10 = 30$ kW.',
        hindi: '$\\Delta K = \\tfrac12 (1500)(400) = 3 \\times 10^5$ जे. $P = 3\\times 10^5/10 = 30$ किलोवाट।',
          },
        },
      ],
    },
    {
      id: 'collisions',
      title: {
        english: 'Collisions',
        hindi: 'टक्कर',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'In **all** collisions, total linear momentum is conserved (no external impulse). Kinetic energy is conserved only in **elastic** collisions.',
        hindi: '**सभी** टकरावों में, कुल रैखिक गति संरक्षित रहती है (कोई बाहरी आवेग नहीं)। गतिज ऊर्जा केवल **लोचदार** टकरावों में संरक्षित रहती है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: '1D Elastic collision',
        hindi: '1डी लोचदार टक्कर',
          },
          statement: {
            english: `Bodies of mass $m_1, m_2$ with initial velocities $u_1, u_2$. Final velocities:
$$v_1 = \\dfrac{(m_1 - m_2) u_1 + 2 m_2 u_2}{m_1 + m_2},\\quad v_2 = \\dfrac{(m_2 - m_1) u_2 + 2 m_1 u_1}{m_1 + m_2}.$$
**Special case** $m_1 = m_2$: velocities exchange. **Special case** $m_2 \\gg m_1, u_2 = 0$: $v_1 \\approx -u_1$ (rebound).`,
            hindi: 'द्रव्यमान के पिंड $m_1, m_2$ प्रारंभिक वेग के साथ $u_1, u_2$। अंतिम वेग: $$v_1 = \\dfrac{(m_1 - m_2) u_1 + 2 m_2 u_2}{m_1 + m_2},\\quad v_2 = \\dfrac{(m_2 - m_1) u_2 + 2 m_1 u_1}{m_1 + m_2}.$$ **विशेष मामला** $m_1 = m_2$: वेग विनिमय। **विशेष मामला** $m_2 \\gg m_1, u_2 = 0$: $v_1 \\approx -u_1$ (रिबाउंड)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Coefficient of restitution** $e = \\dfrac{|v_2 - v_1|}{|u_1 - u_2|}$. Elastic: $e = 1$. Perfectly inelastic: $e = 0$ (stick together).',
        hindi: '**पुनर्प्राप्ति का गुणांक** $e = \\dfrac{|v_2 - v_1|}{|u_1 - u_2|}$। इलास्टिक: $e = 1$. बिल्कुल बेलोचदार: $e = 0$ (एक साथ रहें)।',
            },
            {
              english: '**Perfectly inelastic 1D**: $v = (m_1 u_1 + m_2 u_2)/(m_1 + m_2)$. KE loss $= \\dfrac{m_1 m_2}{2(m_1 + m_2)}(u_1 - u_2)^2$.',
        hindi: '**पूरी तरह से बेलोचदार 1D**: $v = (m_1 u_1 + m_2 u_2)/(m_1 + m_2)$। केई हानि $= \\dfrac{m_1 m_2}{2(m_1 + m_2)}(u_1 - u_2)^2$.',
            },
            {
              english: '**Ball dropped from height $h$, $e$**: speed after $n$th bounce $= \\sqrt{2gh}\\,e^n$; height $= h e^{2n}$. Total path $= h\\dfrac{1+e^2}{1-e^2}$.',
        hindi: '**गेंद ऊंचाई से गिरी $h$, $e$**: $n$वीं उछाल के बाद गति $= \\sqrt{2gh}\\,e^n$; ऊंचाई $= h e^{2n}$. कुल पथ $= h\\dfrac{1+e^2}{1-e^2}$.',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'W = \\vec F \\cdot \\vec d',
      desc: {
        english: 'Work (constant force)',
        hindi: 'कार्य (निरंतर बल)',
      },
    },
    {
      latex: 'K = \\tfrac12 m v^2 = p^2/(2m)',
      desc: {
        english: 'Kinetic energy',
        hindi: 'गतिज ऊर्जा',
      },
    },
    {
      latex: 'W_{\\text{net}} = \\Delta K',
      desc: {
        english: 'Work–energy theorem',
        hindi: 'कार्य-ऊर्जा प्रमेय',
      },
    },
    {
      latex: 'F = -dU/dx',
      desc: {
        english: 'Conservative force ↔ potential',
        hindi: 'रूढ़िवादी बल ↔ क्षमता',
      },
    },
    {
      latex: 'P = \\vec F \\cdot \\vec v',
      desc: {
        english: 'Instantaneous power',
        hindi: 'तात्कालिक शक्ति',
      },
    },
    {
      latex: 'e = (v_2 - v_1)/(u_1 - u_2)',
      desc: {
        english: 'Restitution',
        hindi: 'पुनर्स्थापन',
      },
    },
    {
      latex: 'v = (m_1 u_1 + m_2 u_2)/(m_1 + m_2)',
      desc: {
        english: 'Perfectly inelastic',
        hindi: 'बिल्कुल बेलोचदार',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'WET',
      problem: {
        english: 'A particle of mass 0.5 kg moves under $F = 6x$ N from $x = 0$ to $x = 2$ m. If initially at rest, find final speed.',
        hindi: '0.5 किलोग्राम द्रव्यमान का एक कण $F = 6x$ N के अंतर्गत $x = 0$ से $x = 2$ मीटर तक चलता है। यदि शुरू में आराम पर है, तो अंतिम गति ज्ञात करें।',
      },
      answer: {
        english: '$v = \\sqrt{48} \\approx 6.93$ m/s',
        hindi: '$v = \\sqrt{48} \\approx 6.93$ मी/से',
      },
      solution: {
        english: '$W = \\int_0^2 6x\\,dx = 12$ J $= \\tfrac12 (0.5) v^2 \\Rightarrow v^2 = 48$.',
        hindi: '$W = \\int_0^2 6x\\,dx = 12$ J $= \\tfrac12 (0.5) v^2 \\Rightarrow v^2 = 48$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Power',
      problem: {
        english: 'A pump lifts 100 kg of water per second to a height of 10 m. Power required (no losses, $g=10$)?',
        hindi: 'एक पंप प्रति सेकंड 100 किलोग्राम पानी 10 मीटर की ऊंचाई तक उठाता है। बिजली की आवश्यकता है (कोई हानि नहीं, $g=10$)?',
      },
      answer: {
        english: '$10$ kW',
        hindi: '$10$ किलोवाट',
      },
      solution: {
        english: '$P = (dm/dt) g h = 100 \\cdot 10 \\cdot 10 = 10^4$ W.',
        hindi: '$P = (dm/dt) g h = 100 \\cdot 10 \\cdot 10 = 10^4$ W.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Collision',
      problem: {
        english: 'A bullet of mass 10 g moving at 400 m/s embeds in a 990 g block at rest. Speed after collision?',
        hindi: '10 ग्राम द्रव्यमान की एक गोली 400 m/s पर चलती हुई 990 ग्राम के ब्लॉक में आराम करती है। टक्कर के बाद गति?',
      },
      answer: {
        english: '$4$ m/s',
        hindi: '$4$ m/s',
      },
      solution: {
        english: '$v = (0.01)(400)/(1.0) = 4$ m/s.',
        hindi: '$v = (0.01)(400)/(1.0) = 4$ मी/से.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Energy conservation',
      problem: {
        english: 'A spring of force constant 800 N/m is compressed by 5 cm. Energy stored?',
        hindi: '800 N/m बल स्थिरांक का एक स्प्रिंग 5 सेमी तक संपीड़ित होता है। ऊर्जा संग्रहीत?',
      },
      answer: {
        english: '$1$ J',
        hindi: '$1$ जे',
      },
      solution: {
        english: '$U = \\tfrac12 k x^2 = \\tfrac12 (800)(0.05)^2 = 1$ J.',
        hindi: '$U = \\tfrac12 k x^2 = \\tfrac12 (800)(0.05)^2 = 1$ J.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Restitution',
      problem: {
        english: 'A ball is dropped from 5 m onto a floor with $e = 0.8$. Height after first bounce?',
        hindi: 'एक गेंद को 5 मीटर से $e = 0.8$ के साथ फर्श पर गिराया जाता है। पहली उछाल के बाद ऊँचाई?',
      },
      answer: {
        english: '$h e^2 = 5 \\cdot 0.64 = 3.2$ m',
        hindi: '$h e^2 = 5 \\cdot 0.64 = 3.2$ मी',
      },
      solution: {
        english: 'Height after $n$th bounce $= h e^{2n}$.',
        hindi: '$n$वीं उछाल $= h e^{2n}$ के बाद ऊंचाई।',
      },
    },
  ],
  next: {
    slug: 'rotational-motion',
    title: 'System of Particles & Rotational Motion',
  },
};

export default chapter;
